import { AlarmModeEnum, BatteryStoreModeEnum, DroneBatteryModeEnum, LinkWorkModeEnum } from '/@/types/airport-tsa'
// Dock command set
export enum DeviceCmd {
  // Simple commands
  DebugModeOpen = 'debug_mode_open', // Enable debug mode
  DebugModeClose = 'debug_mode_close', // Disable debug mode
  SupplementLightOpen = 'supplement_light_open', // Turn on fill light
  SupplementLightClose = 'supplement_light_close', // Turn off fill light
  ReturnHome = 'return_home', // Return to home
  ReturnHomeCancel = 'return_home_cancel', // Cancel return to home
  // Complex commands
  DeviceReboot = 'device_reboot', // Dock restart
  DroneOpen = 'drone_open', // Aircraft power on
  DroneClose = 'drone_close', // Aircraft power off
  // DeviceCheck = 'device_check', // One-click troubleshooting (pre-takeoff self-check)
  DeviceFormat = 'device_format', // Format dock storage
  DroneFormat = 'drone_format', // Format aircraft storage
  CoverOpen = 'cover_open', // Open dock cover
  CoverClose = 'cover_close', // Close dock cover
  PutterOpen = 'putter_open', // Extend putter
  PutterClose = 'putter_close', // Retract putter
  ChargeOpen = 'charge_open', // Start charging
  ChargeClose = 'charge_close', // Stop charging
  AlarmStateSwitch = 'alarm_state_switch', // Dock sound & light alarm
  BatteryStoreModeSwitch = 'battery_store_mode_switch', // Battery maintenance
  DroneBatteryModeSwitch = 'battery_maintenance_switch', // Aircraft battery maintenance
  SdrWorkModeSwitch = 'sdr_workmode_switch', // Enhanced video transmission
}

export type DeviceCmdItemAction = AlarmModeEnum | BatteryStoreModeEnum | DroneBatteryModeEnum | LinkWorkModeEnum

export interface DeviceCmdItem{
  label: string, // Title
  status: string, // Current status
  operateText: string, // Button text
  cmdKey: DeviceCmd, // Request command
  oppositeCmdKey?: DeviceCmd, // Opposite-state command
  action?: DeviceCmdItemAction, // Parameter
  func: string, // Handler function
  loading: boolean // Button loading
  disabled?: boolean // Button disabled
}
export const noDebugCmdList: DeviceCmdItem[] = [
  {
    label: 'Return Home',
    status: '--',
    operateText: 'Return Home',
    cmdKey: DeviceCmd.ReturnHome,
    func: 'returnHome',
    loading: false,
  },
  {
    label: 'Return Home Cancel',
    status: '--',
    operateText: 'Return Home Cancel',
    cmdKey: DeviceCmd.ReturnHomeCancel,
    func: 'returnHomeCancel',
    loading: false,
  }
]

// Dock commands
export const cmdList: DeviceCmdItem[] = [
  {
    // iconName: ,
    label: 'Dock system',
    status: 'Working',
    operateText: 'Restart',
    cmdKey: DeviceCmd.DeviceReboot,
    func: 'deviceReboot',
    loading: false,
    // btnAnimationIconName: '',
    // operateTips: '',
    // statusColor: '',
  },
  {
    label: 'Aircraft',
    status: 'Powered off',
    operateText: 'Power on',
    cmdKey: DeviceCmd.DroneOpen,
    oppositeCmdKey: DeviceCmd.DroneClose,
    func: 'droneStatus',
    loading: false,
  },
  {
    label: 'Dock cover',
    status: 'Closed',
    operateText: 'Open',
    cmdKey: DeviceCmd.CoverOpen,
    oppositeCmdKey: DeviceCmd.CoverClose,
    func: 'coverStatus',
    loading: false,
  },
  {
    label: 'Putter',
    status: 'Retracted',
    operateText: 'Extend',
    cmdKey: DeviceCmd.PutterOpen,
    oppositeCmdKey: DeviceCmd.PutterClose,
    func: 'putterStatus',
    loading: false,
  },
  {
    label: 'Charging status',
    status: 'Not charging',
    operateText: 'Charge',
    cmdKey: DeviceCmd.ChargeOpen,
    oppositeCmdKey: DeviceCmd.ChargeClose,
    func: 'chargeStatus',
    loading: false,
  },
  {
    label: 'Dock storage',
    status: '--',
    operateText: 'Format',
    cmdKey: DeviceCmd.DeviceFormat,
    func: 'deviceFormat',
    loading: false,
  },
  {
    label: 'Aircraft storage',
    status: '--',
    operateText: 'Format',
    cmdKey: DeviceCmd.DroneFormat,
    func: 'droneFormat',
    loading: false,
  },
  {
    label: 'Fill light',
    status: 'Off',
    operateText: 'Turn on',
    cmdKey: DeviceCmd.SupplementLightOpen,
    oppositeCmdKey: DeviceCmd.SupplementLightClose,
    func: 'supplementLightStatus',
    loading: false,
  },
  {
    label: 'Dock sound & light alarm',
    status: 'Off',
    operateText: 'Turn on',
    cmdKey: DeviceCmd.AlarmStateSwitch,
    action: AlarmModeEnum.OPEN,
    func: 'alarmState',
    loading: false,
  },
  {
    label: 'Dock battery storage mode',
    status: 'Planned',
    operateText: 'Emergency',
    cmdKey: DeviceCmd.BatteryStoreModeSwitch,
    action: BatteryStoreModeEnum.BATTERY_EMERGENCY_STORE,
    func: 'batteryStoreMode',
    loading: false,
  },
  {
    label: 'Aircraft battery maintenance',
    status: '--',
    operateText: 'Maintain',
    cmdKey: DeviceCmd.DroneBatteryModeSwitch,
    action: DroneBatteryModeEnum.OPEN,
    func: 'droneBatteryMode',
    loading: false,
    disabled: true,
  },
  {
    label: '4G enhancement',
    status: '--',
    operateText: 'Enable',
    cmdKey: DeviceCmd.SdrWorkModeSwitch,
    action: LinkWorkModeEnum.FourG_FUSION_MODE,
    func: 'sdrWorkMode',
    loading: false,
  },
]

export enum DeviceCmdStatusText {
  DeviceRebootNormalText = 'Working',
  DeviceRebootInProgressText = 'Restarting...',
  DeviceRebootFailedText = 'Restart failed',

  DroneStatusOpenNormalText = 'On',
  DroneStatusOpenInProgressText = 'Powering on...',
  DroneStatusOpenFailedText = 'Off',
  DroneStatusOpenBtnText = 'Power off',

  DroneStatusCloseNormalText = 'Off',
  DroneStatusCloseInProgressText = 'Powering off...',
  DroneStatusCloseFailedText = 'On',
  DroneStatusCloseBtnText = 'Power on',

  DeviceCoverOpenNormalText = 'Open',
  DeviceCoverOpenInProgressText = 'Opening...',
  DeviceCoverOpenFailedText = 'Closed',
  DeviceCoverOpenBtnText = 'Close',

  DeviceCoverCloseNormalText = 'Closed',
  DeviceCoverCloseInProgressText = 'Closing...',
  DeviceCoverCloseFailedText = 'Open',
  DeviceCoverCloseBtnText = 'Open',

  DevicePutterOpenNormalText = 'Extended',
  DevicePutterOpenBtnText = 'Retract',
  DevicePutterOpenInProgressText = 'Putter extending',
  DevicePutterOpenFailedText = 'Retracted',

  DevicePutterCloseNormalText = 'Retracted',
  DevicePutterCloseInProgressText = 'Putter retracting',
  DevicePutterCloseFailedText = 'Extended',
  DevicePutterCloseBtnText = 'Extend',

  DeviceChargeOpenNormalText = 'Charging',
  DeviceChargeOpenInProgressText = 'Charging...',
  DeviceChargeOpenFailedText = 'Not charging',
  DeviceChargeOpenBtnText = 'Stop charging',

  DeviceChargeCloseNormalText = 'Not charging',
  DeviceChargeCloseInProgressText = 'Stopping charge...',
  DeviceChargeCloseFailedText = 'Charging',
  DeviceChargeCloseBtnText = 'Charge',

  DeviceFormatInProgressText = 'Formatting...',
  DeviceFormatFailedText = 'Format failed',

  DroneFormatInProgressText = 'Formatting...',
  DroneFormatFailedText = 'Format failed',

  DeviceSupplementLightOpenNormalText = 'On',
  DeviceSupplementLightOpenInProgressText = 'Turning on...',
  DeviceSupplementLightOpenFailedText = 'Off',
  DeviceSupplementLightOpenBtnText = 'Turn off',

  DeviceSupplementLightCloseNormalText = 'Off',
  DeviceSupplementLightCloseText = 'Turning off...',
  DeviceSupplementLightCloseFailedText = 'On',
  DeviceSupplementLightCloseBtnText = 'Turn on',

  AlarmStateOpenNormalText = 'On',
  AlarmStateOpenText = 'Turning on...',
  AlarmStateOpenFailedText = 'Off',
  AlarmStateOpenBtnText = 'Turn off',

  AlarmStateCloseNormalText = 'Off',
  AlarmStateCloseText = 'Turning off...',
  AlarmStateCloseFailedText = 'On',
  AlarmStateCloseBtnText = 'Turn on',

  BatteryStoreModePlanNormalText = 'Planned',
  BatteryStoreModePlanText = 'Switching...',
  BatteryStoreModePlanFailedText = 'Emergency',
  BatteryStoreModePlanBtnText = 'Emergency',

  BatteryStoreModeEmergencyNormalText = 'Emergency',
  BatteryStoreModeEmergencyText = 'Switching...',
  BatteryStoreModeEmergencyFailedText = 'Planned',
  BatteryStoreModeEmergencyBtnText = 'Planned',

  DroneBatteryModeMaintenanceInProgressText = 'Maintaining',
  DroneBatteryModeMaintenanceNotNeedText = 'No maintenance needed',
  DroneBatteryModeMaintenanceNeedText = 'Maintenance needed',
  DroneBatteryModeOpenBtnText = 'Maintain',
  DroneBatteryModeCloseBtnText = 'Stop maintenance',

  SdrWorkModeFourGOpenNormalText = 'On',
  SdrWorkModeFourGOpenText = 'Enabling...',
  SdrWorkModeFourGOpenFailedText = '--',
  SdrWorkModeFourGOpenBtnText = 'Disable',

  SdrWorkModeFourGCloseNormalText = '--',
  SdrWorkModeFourGCloseText = 'Disabling...',
  SdrWorkModeFourGCloseFailedText = 'On',
  SdrWorkModeFourCloseBtnText = 'Enable',
}

// Command WS message status
export enum DeviceCmdExecuteStatus {
  Sent = 'sent', // Sent
  InProgress = 'in_progress', // In progress
  OK = 'ok', // Succeeded
  Failed = 'failed', // Failed
  Canceled = 'canceled', // Canceled
  Timeout = 'timeout' // Timeout
}

export interface DeviceCmdExecuteInfo {
  biz_code: string,
  timestamp: number,
  sn: string,
  bid: string,
  output:{
    status: DeviceCmdExecuteStatus,
    progress?: {
      percent: number,
      step_key: string,
      step_result: number
    },
    ext?: {
      rate?: number
    }
  }
  result: number,
}

// Command execution status of all docks
export interface DevicesCmdExecuteInfo {
  [key: string]: DeviceCmdExecuteInfo[], // sn --- DeviceCmdExecuteInfo
}
